import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Columns2, Copy, Link2, Loader2, Printer, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "../components/ui/skeleton";
import { API, errMsg } from "../lib/api";
import { DIM, DIMENSIONS, BANDS, VERDICTS, nearestAnchor } from "../lib/bopta";
import { LadderCard, spreadLine } from "../components/Ladder";
import { BoptaRadar, DimBars } from "../components/Charts";
import { VBCta } from "../components/VBCta";
import { ScenarioCard, ScenarioMatrix } from "../components/Scenarios";
import { ComparePanel } from "../components/ComparePanel";
import { otherLens } from "../lib/device";

const useOtherLens = (a, id) => {
  const compareWith = useLocation().state?.compareWith;
  const [other, setOther] = useState(null);
  useEffect(() => {
    setOther(null);
    const oid = a ? otherLens(id, compareWith) : null;
    if (!oid || oid === id) return;
    API.get(`/assessments/${oid}`).then((r) => r.data.mode !== a.mode && setOther(r.data)).catch(() => {});
  }, [a, id, compareWith]);
  return other;
};

const copy = (text, msg) => navigator.clipboard.writeText(text).then(() => toast.success(msg));

const List = ({ title, items, testid }) => (
  <div className="panel p-6" data-testid={testid}>
    <p className="kicker">{title}</p>
    <ul className="mt-3 space-y-2 text-sm">{(items || []).map((x) => <li key={x} className="muted">→ {x}</li>)}</ul>
  </div>
);

const PlanBody = ({ plan }) => (
  <div className="space-y-5" data-testid="plan-content">
    <div className="panel p-7">
      <p className="text-xl font-bold" data-testid="plan-headline">{plan.headline}</p>
      <p className="mt-3 muted">{plan.summary}</p>
    </div>
    <div className="grid gap-4 md:grid-cols-2">
      <List title="Strengths" items={plan.strengths} testid="plan-strengths" />
      <List title="Bottlenecks" items={plan.bottlenecks} testid="plan-bottlenecks" />
    </div>
    <div className="grid gap-4 md:grid-cols-3">
      <List title="Next 7 days" items={plan.immediate} testid="plan-immediate" />
      <List title="30 days" items={plan.day30} testid="plan-day30" />
      <List title="90 days" items={plan.day90} testid="plan-day90" />
    </div>
    <div className="grid gap-4 md:grid-cols-3">
      {(plan.competencies || []).map((c) => <div key={c.name} className="panel p-5"><p className="font-bold">{c.name}</p><p className="mt-1 text-sm muted">{c.why}</p></div>)}
    </div>
    {plan.decision_boundary && <p className="rounded-xl bg-[var(--tint)] p-5" data-testid="plan-boundary"><b>Propose this boundary:</b> {plan.decision_boundary}</p>}
    <div className="panel p-7" data-testid="manager-script">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xl font-bold">Your manager conversation</p>
        <button className="btn btn-line no-print" onClick={() => copy(plan.manager_script, "Script copied")} data-testid="manager-script-copy"><Copy className="h-4 w-4" /> Copy</button>
      </div>
      <blockquote className="mt-4 whitespace-pre-line border-l-4 border-[var(--teal)] pl-5">{plan.manager_script}</blockquote>
      <ul className="mt-5 space-y-1 text-sm muted">{(plan.manager_questions || []).map((q) => <li key={q}>? {q}</li>)}</ul>
    </div>
  </div>
);

const Plan = ({ id, initial }) => {
  const [plan, setPlan] = useState(initial);
  const [busy, setBusy] = useState(false);
  const started = useRef(false);
  const gen = useCallback(async (refresh) => {
    setBusy(true);
    try {
      const r = await API.post(`/assessments/${id}/plan`, null, { params: refresh ? { refresh: true } : {} });
      setPlan(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  }, [id]);
  useEffect(() => {
    if (initial || started.current) return;
    started.current = true;
    gen(false);
  }, [initial, gen]);

  if (!plan && busy) return <div className="panel p-7" data-testid="plan-loading"><p className="flex items-center gap-2 muted"><Loader2 className="h-4 w-4 animate-spin" /> Building your personalised plan (about 30 seconds)…</p><Skeleton className="mt-4 h-4 w-2/3" /></div>;
  if (!plan) return <button className="btn btn-main" onClick={() => gen(false)} data-testid="plan-generate"><Sparkles className="h-4 w-4" /> Generate my plan</button>;
  return (
    <>
      <button className="btn btn-line mb-5 no-print" disabled={busy} onClick={() => gen(true)} data-testid="plan-regenerate">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Regenerate</button>
      <PlanBody plan={plan} />
    </>
  );
};

const matrixKey = (a) => a.scenarios.find((k) => k !== "E");

const MatrixPosition = ({ a }) => {
  const k = matrixKey(a);
  const manager = a.mode === "manager";
  return (
    <div className="space-y-5 avoid-break" data-testid="results-matrix-position">
      <ScenarioCard k={k} manager={manager} testid={`results-scenario-${k}`} />
      <div className="panel p-7"><p className="kicker mb-4">{manager ? "Where your team sits" : "Where you sit"} on the matrix</p><ScenarioMatrix active={k} manager={manager} /></div>
    </div>
  );
};

const Lowest = ({ a }) => {
  const low = DIM[a.lowest];
  return (
    <div className="panel p-7" data-testid="results-lowest">
      <p className="kicker">First area to explore</p>
      <p className="mt-2 text-2xl font-bold">{low.name} · {Math.round(a.dims[low.k])}</p>
      <div className="mt-4 space-y-2">
        {low.questions.map((q, i) => {
          const near = nearestAnchor(q.anchors, a.answers[low.k][i]);
          return <p key={q.q} className="rounded-lg p-3 text-sm" style={{ background: low.tint }}><b>{a.answers[low.k][i]} · {near.t}</b> <span className="muted">— {near.d}</span></p>;
        })}
      </div>
    </div>
  );
};

const Bandwidth = ({ a }) => {
  if (a.bandwidth == null) {
    return (
      <div className="panel p-7" data-testid="bandwidth-card">
        <p className="kicker">Mental bandwidth</p>
        <p className="mt-2 text-2xl font-bold">Not measured</p>
        <p className="mt-2 text-sm muted">{a.mode === "manager" ? "The manager lens rates the team, so the Broken Machine test is skipped." : "This result was saved before the bandwidth lens was added. Retake the assessment to see it."}</p>
      </div>
    );
  }
  const [title, line] = VERDICTS[a.verdict];
  return (
    <>
      <div className="panel p-7" data-testid="bandwidth-card">
        <p className="kicker">Mental bandwidth</p>
        <p className="display mt-2 text-5xl font-extrabold text-[var(--teal)]" data-testid="bandwidth-score">{Math.round(a.bandwidth)}<span className="text-lg muted"> / 100</span></p>
        <p className="mt-2 text-xl font-bold" data-testid="bandwidth-band">{a.band}</p>
        <p className="mt-1 text-sm muted">{BANDS[a.band]}</p>
        <p className="mt-4 text-xs muted">Combines your planning horizon (40%) and the Broken Machine case (60%). A reflection signal, not a test of worth.</p>
      </div>
      <div className="rounded-2xl p-7" style={{ background: a.verdict === "aligned" ? "var(--tint)" : "#FFEEE5" }} data-testid="capacity-verdict">
        <p className="kicker">Capacity vs. autonomy</p>
        <p className="mt-2 text-2xl font-bold" data-testid="capacity-verdict-title">{title}</p>
        <p className="mt-2 muted">{line}</p>
        {a.bottlenecks?.length > 0 && <p className="mt-4 text-sm"><b>Bottleneck flags:</b> {a.bottlenecks.map((k) => DIM[k].name).join(", ")}. You plan far ahead here but hold little authority.</p>}
      </div>
    </>
  );
};

export default function Results() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    API.get(`/assessments/${id}`).then((r) => setA(r.data)).catch(() => setErr(true));
  }, [id]);

  const series = useMemo(() => (a ? [{ name: "You", dims: a.dims, color: "#1B5C58" }] : []), [a]);
  const other = useOtherLens(a, id);
  if (err) return <div className="wrap py-24 text-center"><h1 className="text-4xl font-bold">Result not found</h1><Link to="/assessment" className="btn btn-main mt-6">Take the assessment</Link></div>;
  if (!a) return <div className="wrap space-y-4 py-16"><Skeleton className="h-12 w-1/2" /><Skeleton className="h-64 w-full" /></div>;

  return (
    <div className="wrap py-12" data-testid="results-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="kicker">Your operational reality · {a.mode === "manager" ? "Manager lens" : "Staff lens"}</p>
          {a.working === false && <span className="mt-2 inline-block rounded-full bg-[#FFEEE5] px-3 py-1 text-xs font-semibold text-[var(--orange)]" data-testid="past-role-tag">Based on a past role</span>}
          <h1 className="mt-2 text-4xl font-extrabold sm:text-5xl">Your title tells one story. <span className="text-[var(--teal)]">Your autonomy tells another.</span></h1>
        </div>
        <div className="flex flex-wrap gap-2 no-print">
          {!other && (
            <Link to={a.mode === "manager" ? "/assessment" : "/assessment?mode=manager"} className="btn btn-main" title={a.mode === "manager" ? "Score your own autonomy too" : "Lead a team? Score their independence too"} data-testid="results-other-lens">
              <Columns2 className="h-4 w-4" /> Take the {a.mode === "manager" ? "staff" : "manager"} lens
            </Link>
          )}
          <button className="btn btn-line" onClick={() => copy(window.location.href, "Private link copied")} data-testid="results-share"><Link2 className="h-4 w-4" /> Share</button>
          <button className="btn btn-line" onClick={() => window.print()} data-testid="results-print"><Printer className="h-4 w-4" /> PDF</button>
        </div>
      </div>

      <section className="mt-10 grid gap-5 lg:grid-cols-3">
        <div className="rounded-2xl bg-[var(--ink)] p-7 text-white" data-testid="results-score-panel">
          <p className="text-sm text-white/60">Overall autonomy</p>
          <p className="display mt-2 text-7xl font-extrabold" data-testid="results-overall">{Math.round(a.overall)}<span className="text-xl text-white/50"> / 100</span></p>
          <div className="mt-4 h-2 rounded-full bg-white/10"><div className="h-2 rounded-full bg-[#7FD1C4]" style={{ width: `${a.overall}%` }} /></div>
          <div className="mt-5 space-y-3 border-t border-white/10 pt-5 text-sm">
            <p><span className="block text-white/50">Role</span><span data-testid="results-role">{a.role_label}</span></p>
            <p><span className="block text-white/50">Reports to</span>{a.manager_label}</p>
          </div>
        </div>
        <div className="panel p-5"><p className="kicker">BOPTA profile</p><BoptaRadar series={series} height={280} /></div>
        <div className="panel p-7"><p className="kicker mb-2">Dependency map</p><p className="mb-5 text-sm font-medium" data-testid="level-spread">{spreadLine(a.dims, Object.fromEntries(DIMENSIONS.map((x) => [x.k, x.name])))}</p><DimBars dims={a.dims} lowest={a.lowest} horizon={a.horizon} bottlenecks={a.bottlenecks || []} /></div>
      </section>

      {other && <ComparePanel current={a} other={other} />}

      <section className="mt-5 grid gap-5 lg:grid-cols-2">
        <LadderCard score={a.overall} manager={a.mode === "manager"} />
        {matrixKey(a) && <MatrixPosition a={a} />}
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-2">
        <Bandwidth a={a} />
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-2">
        {a.scenarios.includes("E") && <ScenarioCard k="E" testid="results-scenario-E" />}
        <Lowest a={a} />
      </section>

      <section className="mt-16">
        <p className="kicker flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> AI development plan</p>
        <h2 className="mb-6 mt-2 text-3xl font-bold">Build the trust behind autonomy.</h2>
        <Plan id={id} initial={a.plan} />
      </section>

      <section className="mt-16 no-print"><VBCta title="Is the founder the real ceiling?" /></section>
      <p className="mt-8 text-xs muted">A signal, not a label. Scores are self-reported. Don't use them alone for employment decisions.</p>
    </div>
  );
}
